'use client';

/**
 * Thin re-export of Radix Popover primitives.
 *
 * Dashboard / marketing should import these from `@clickt/brand` rather than
 * pulling Radix in directly, so the dependency stays declared in one place.
 *
 * Usage:
 *   <Popover.Root open={open} onOpenChange={setOpen}>
 *     <Popover.Trigger asChild>...</Popover.Trigger>
 *     <Popover.Portal>
 *       <Popover.Content>...</Popover.Content>
 *     </Popover.Portal>
 *   </Popover.Root>
 *
 * The Content variant exported here is a styled default (card, shadow, animation)
 * matched to the brand. Pass `className` to override; pass `unstyled` to drop
 * the defaults entirely for one-off use.
 */

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';
import { cn } from '../utils.ts';

export const Root = PopoverPrimitive.Root;
export const Trigger = PopoverPrimitive.Trigger;
export const Portal = PopoverPrimitive.Portal;
export const Anchor = PopoverPrimitive.Anchor;
export const Close = PopoverPrimitive.Close;

interface ContentProps extends ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /** Drop the default card/shadow styling (for fully-custom popovers). */
  unstyled?: boolean;
}

export const Content = forwardRef<ElementRef<typeof PopoverPrimitive.Content>, ContentProps>(
  ({ className, sideOffset = 4, unstyled, ...props }, ref) => (
    <PopoverPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // rounded-md (16px), not rounded-card (24px): dropdown panels match
        // DropdownMenu, and the tighter corner keeps inset scrollbars visible.
        !unstyled &&
          'z-[60] rounded-md border border-light-silver bg-surface shadow-hover overflow-hidden origin-top',
        !unstyled &&
          'data-[state=open]:animate-popover-show data-[state=closed]:animate-popover-hide motion-reduce:animate-none',
        className,
      )}
      {...props}
    />
  ),
);
Content.displayName = 'PopoverContent';
